/**
 * ShapeStyleToolbar - Toolbar for customizing shape properties
 * Supports gradients, shadows, stroke styles, and more
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Palette,
  X,
  Minus,
  GripVertical,
  Circle,
  Square,
  Type,
  Layers,
  Droplet,
  Eye,
  ArrowRight,
} from 'lucide-react';
import { EditableElement, GradientFill, ShadowEffect, StrokeStyle, BlurEffect, BlendMode, CornerRadius, StarConfig, PolygonConfig, ArrowConfig } from '../editing/types';
import { createDefaultGradient } from '../utils/gradientUtils';
import { createDefaultShadow, createDefaultStrokeStyle, createDefaultBlur } from '../utils/shapeStyleUtils';

interface ShapeStyleToolbarProps {
  shapes: EditableElement[];
  onUpdate: (updates: Partial<EditableElement['data']>) => void;
  position?: { x: number; y: number };
  /** Bounds of the selected element(s) in screen coordinates */
  elementBounds?: { x: number; y: number; width: number; height: number };
  /** Container element for viewport bounds calculation */
  containerRef?: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
}

export const ShapeStyleToolbar: React.FC<ShapeStyleToolbarProps> = ({
  shapes,
  onUpdate,
  position,
  elementBounds,
  containerRef,
  onClose,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showGradientPicker, setShowGradientPicker] = useState(false);
  const [showShadowMenu, setShowShadowMenu] = useState(false);
  const [showStrokeMenu, setShowStrokeMenu] = useState(false);
  const [showStrokeColorPicker, setShowStrokeColorPicker] = useState(false);
  const [showBlurMenu, setShowBlurMenu] = useState(false);
  const [showBlendMenu, setShowBlendMenu] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarSize, setToolbarSize] = useState({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [isPositionedAbove, setIsPositionedAbove] = useState(false);
  
  // Measure toolbar size
  useEffect(() => {
    if (toolbarRef.current) {
      const rect = toolbarRef.current.getBoundingClientRect();
      setToolbarSize({ width: rect.width, height: rect.height });
    }
  }, [shapes]);

  // Trigger animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const gradientPickerRef = useRef<HTMLDivElement>(null);
  const shadowMenuRef = useRef<HTMLDivElement>(null);
  const strokeMenuRef = useRef<HTMLDivElement>(null);
  const strokeColorPickerRef = useRef<HTMLDivElement>(null);
  const blurMenuRef = useRef<HTMLDivElement>(null);
  const blendMenuRef = useRef<HTMLDivElement>(null);

  // Get representative data (first shape) for display
  const data = shapes[0]?.data;
  if (!data) return null;

  // Check if a property has mixed values across shapes
  const hasMixedValues = (prop: keyof EditableElement['data']): boolean => {
    if (shapes.length <= 1) return false;
    const values = shapes.map(s => s.data?.[prop]);
    return new Set(values).size > 1;
  };

  // Get common value or null if mixed
  const getCommonValue = <T,>(prop: keyof EditableElement['data']): T | null => {
    if (hasMixedValues(prop)) return null;
    return data[prop] as T;
  };

  // Extract common values for UI
  const fillColor = getCommonValue<string>('fill');
  const fillGradient = getCommonValue<GradientFill>('fillGradient');
  const strokeColor = getCommonValue<string>('stroke');
  const strokeWidth = getCommonValue<number>('strokeWidth');
  const shadows = getCommonValue<ShadowEffect[]>('shadows');
  const strokeStyle = getCommonValue<StrokeStyle>('strokeStyle');
  const blur = getCommonValue<BlurEffect>('blur');
  const blendMode = getCommonValue<BlendMode>('blendMode');
  const opacity = getCommonValue<number>('opacity');
  const cornerRadius = getCommonValue<CornerRadius | number>('cornerRadius');
  const starConfig = getCommonValue<StarConfig>('starConfig');
  const polygonConfig = getCommonValue<PolygonConfig>('polygonConfig');
  const arrowConfig = getCommonValue<ArrowConfig>('arrowConfig');
  
  // Get shape type to show relevant controls
  const shapeType = getCommonValue<string>('shapeType');

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowColorPicker(false);
      }
      if (
        gradientPickerRef.current &&
        !gradientPickerRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowGradientPicker(false);
      }
      if (
        shadowMenuRef.current &&
        !shadowMenuRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowShadowMenu(false);
      }
      if (
        strokeMenuRef.current &&
        !strokeMenuRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowStrokeMenu(false);
      }
      if (
        strokeColorPickerRef.current &&
        !strokeColorPickerRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowStrokeColorPicker(false);
      }
      if (
        blurMenuRef.current &&
        !blurMenuRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowBlurMenu(false);
      }
      if (
        blendMenuRef.current &&
        !blendMenuRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowBlendMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate viewport-aware position
  const calculatePosition = () => {
    const TOOLBAR_HEIGHT = 50; // Approximate toolbar height
    const TOOLBAR_MARGIN = 20; // Margin from element
    const VIEWPORT_PADDING = 10; // Padding from viewport edges
    
    // Get container bounds for viewport calculation
    const containerBounds = containerRef?.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight,
    };
    
    let x = position?.x || 0;
    let y = position?.y || 0;
    let positionedAbove = false;
    
    if (elementBounds) {
      // Calculate available space above and below the element
      const spaceAbove = elementBounds.y - containerBounds.top;
      const spaceBelow = containerBounds.bottom - (elementBounds.y + elementBounds.height);
      
      // Position horizontally centered on element
      x = elementBounds.x + elementBounds.width / 2;
      
      // Position above or below based on available space
      if (spaceAbove >= TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Position above element
        y = elementBounds.y - TOOLBAR_MARGIN;
        positionedAbove = true;
      } else if (spaceBelow >= TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Position below element
        y = elementBounds.y + elementBounds.height + TOOLBAR_HEIGHT + TOOLBAR_MARGIN;
        positionedAbove = false;
      } else {
        // Not enough space above or below, position at top of viewport
        y = containerBounds.top + VIEWPORT_PADDING + TOOLBAR_HEIGHT;
        positionedAbove = true;
      }
    } else if (position) {
      // Fallback: Check if there's enough space above the position
      const spaceAbove = position.y - containerBounds.top;
      if (spaceAbove < TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Not enough space above, position below
        y = position.y + TOOLBAR_HEIGHT + TOOLBAR_MARGIN * 2;
        positionedAbove = false;
      } else {
        y = position.y - TOOLBAR_MARGIN;
        positionedAbove = true;
      }
    }
    
    // Clamp horizontal position to stay within viewport
    const toolbarWidth = toolbarSize.width || 400; // Estimated width
    const halfWidth = toolbarWidth / 2;
    x = Math.max(containerBounds.left + halfWidth + VIEWPORT_PADDING, x);
    x = Math.min(containerBounds.right - halfWidth - VIEWPORT_PADDING, x);
    
    return { x, y, positionedAbove };
  };
  
  const calculatedPosition = useMemo(() => calculatePosition(), [
    elementBounds?.x,
    elementBounds?.y,
    elementBounds?.width,
    elementBounds?.height,
    position?.x,
    position?.y,
    toolbarSize.width,
  ]);

  // Update position state when position changes
  useEffect(() => {
    setIsPositionedAbove(calculatedPosition.positionedAbove);
  }, [calculatedPosition.positionedAbove]);
  
  // Determine transform based on position and animation state
  const slideOffset = isPositionedAbove ? '-20px' : '20px';
  const translateY = isVisible ? '0' : slideOffset;
  const transformValue = `translate(-50%, -100%) translateY(${translateY})`;
  
  const toolbarStyle: React.CSSProperties = {
    position: 'absolute',
    left: calculatedPosition.x,
    top: calculatedPosition.y,
    transform: transformValue,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 8px',
    backgroundColor: 'white',
    border: '1px solid rgb(229, 231, 235)',
    borderRadius: '9999px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    opacity: isVisible ? 1 : 0,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const buttonStyle = (isActive: boolean): React.CSSProperties => ({
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: isActive ? 'rgba(0, 134, 201, 0.1)' : 'transparent',
    color: isActive ? '#0086C9' : 'rgb(107, 114, 128)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  });

  const dividerStyle: React.CSSProperties = {
    width: '1px',
    height: '24px',
    backgroundColor: 'rgb(229, 231, 235)',
    margin: '0 4px',
  };

  const presetColors = [
    '#000000', '#374151', '#6B7280', '#9CA3AF',
    '#EF4444', '#F59E0B', '#10B981', '#3B82F6',
    '#8B5CF6', '#EC4899', '#F97316', '#14B8A6',
  ];

  const gradientTypes: Array<{ type: GradientFill['type']; label: string }> = [
    { type: 'linear', label: 'Linear' },
    { type: 'radial', label: 'Radial' },
    { type: 'angular', label: 'Angular' },
    { type: 'diamond', label: 'Diamond' },
  ];

  const strokePatterns: Array<{ pattern: StrokeStyle['pattern']; label: string }> = [
    { pattern: 'solid', label: 'Solid' },
    { pattern: 'dashed', label: 'Dashed' },
    { pattern: 'dotted', label: 'Dotted' },
  ];

  // Determine if shape has gradient or solid fill
  const hasGradient = !!fillGradient;
  const hasMixedFill = hasMixedValues('fill');
  const currentFill = hasGradient ? '#000000' : (fillColor || '#FFFFFF');
  
  // Auto-update color picker when selection changes
  useEffect(() => {
    // Close color picker when selection changes to avoid stale state
    if (showColorPicker && shapes.length === 0) {
      setShowColorPicker(false);
    }
  }, [shapes, showColorPicker]);

  return (
    <div ref={toolbarRef} style={toolbarStyle}>
      {/* Fill Color/Gradient */}
      <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(false)}
          onClick={() => {
            if (hasGradient) {
              setShowGradientPicker(!showGradientPicker);
              setShowColorPicker(false);
            } else {
              setShowColorPicker(!showColorPicker);
              setShowGradientPicker(false);
            }
          }}
          title={hasGradient ? 'Gradient Fill' : 'Fill Color'}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              backgroundColor: currentFill,
              border: hasMixedFill && !hasGradient 
                ? '2px dashed rgb(107, 114, 128)' 
                : '1px solid rgb(229, 231, 235)',
              background: hasGradient
                ? `linear-gradient(135deg, ${fillGradient?.stops[0]?.color || '#FFFFFF'}, ${fillGradient?.stops[fillGradient.stops.length - 1]?.color || '#000000'})`
                : hasMixedFill
                ? 'repeating-linear-gradient(45deg, #FFFFFF, #FFFFFF 4px, #E5E7EB 4px, #E5E7EB 8px)'
                : currentFill,
              position: 'relative',
            }}
            title={hasMixedFill && !hasGradient ? 'Mixed fill colors' : hasGradient ? 'Gradient Fill' : 'Fill Color'}
          />
        </button>

        {/* Solid Color Picker */}
        {showColorPicker && (
          <div
            ref={colorPickerRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '4px',
              zIndex: 1001,
            }}
          >
            {presetColors.map((color) => (
              <button
                key={color}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  backgroundColor: color,
                  border: '1px solid rgb(229, 231, 235)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  onUpdate({ fill: color, fillGradient: undefined });
                  setShowColorPicker(false);
                }}
              />
            ))}
            <input
              type="color"
              value={hasMixedFill ? '#FFFFFF' : (fillColor || '#FFFFFF')}
              onChange={(e) => {
                onUpdate({ fill: e.target.value, fillGradient: undefined });
                setShowColorPicker(false);
              }}
              style={{
                width: '24px',
                height: '24px',
                border: '1px solid rgb(229, 231, 235)',
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '2px',
                opacity: hasMixedFill ? 0.5 : 1,
              }}
              title={hasMixedFill ? 'Mixed colors - click to set uniform color' : 'Custom Color'}
            />
          </div>
        )}

        {/* Gradient Picker */}
        {showGradientPicker && (
          <div
            ref={gradientPickerRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              zIndex: 1001,
              minWidth: '200px',
            }}
          >
            <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
              Gradient Type
            </div>
            {gradientTypes.map((gt) => {
              const isActive = fillGradient?.type === gt.type;
              return (
                <button
                  key={gt.type}
                  style={{
                    ...buttonStyle(isActive),
                    width: '100%',
                    height: '32px',
                    justifyContent: 'flex-start',
                    padding: '0 8px',
                  }}
                  onClick={() => {
                    const newGradient = createDefaultGradient(gt.type);
                    onUpdate({ fillGradient: newGradient, fill: undefined });
                  }}
                >
                  {gt.label}
                </button>
              );
            })}
            <button
              style={{
                ...buttonStyle(false),
                width: '100%',
                height: '32px',
                marginTop: '8px',
                justifyContent: 'flex-start',
                padding: '0 8px',
              }}
              onClick={() => {
                onUpdate({ fillGradient: undefined, fill: fillColor || '#FFFFFF' });
                setShowGradientPicker(false);
              }}
            >
              Remove Gradient
            </button>
          </div>
        )}
      </div>

      <div style={dividerStyle} />

      {/* Stroke Color */}
      <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(false)}
          onClick={() => {
            setShowStrokeColorPicker(!showStrokeColorPicker);
            setShowColorPicker(false);
            setShowGradientPicker(false);
          }}
          title="Stroke Color"
          disabled={hasMixedValues('stroke')}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              backgroundColor: strokeColor || '#000000',
              border: '1px solid rgb(229, 231, 235)',
              opacity: hasMixedValues('stroke') ? 0.5 : 1,
            }}
          />
        </button>

        {/* Stroke Color Picker */}
        {showStrokeColorPicker && (
          <div
            ref={strokeColorPickerRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '4px',
              zIndex: 1001,
            }}
          >
            {presetColors.map((color) => (
              <button
                key={color}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  backgroundColor: color,
                  border: '1px solid rgb(229, 231, 235)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  onUpdate({ stroke: color });
                  setShowStrokeColorPicker(false);
                }}
              />
            ))}
            <input
              type="color"
              value={strokeColor || '#000000'}
              onChange={(e) => {
                onUpdate({ stroke: e.target.value });
                setShowStrokeColorPicker(false);
              }}
              style={{
                width: '24px',
                height: '24px',
                border: '1px solid rgb(229, 231, 235)',
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '2px',
              }}
              title="Custom Stroke Color"
            />
          </div>
        )}
      </div>

      {/* Stroke Width */}
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        {[1, 2, 4, 8].map((width) => {
          const isActive = strokeWidth === width && !hasMixedValues('strokeWidth');
          return (
            <button
              key={width}
              style={buttonStyle(isActive)}
              onClick={() => onUpdate({ strokeWidth: width })}
              title={`${width}px`}
              disabled={hasMixedValues('strokeWidth')}
            >
              <Minus
                size={16}
                strokeWidth={width / 2}
                opacity={hasMixedValues('strokeWidth') ? 0.5 : 1}
              />
            </button>
          );
        })}
      </div>

      {/* Stroke Style */}
      <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(
            (strokeStyle?.pattern !== 'solid' && strokeStyle?.pattern !== undefined) &&
            !hasMixedValues('strokeStyle')
          )}
          onClick={() => setShowStrokeMenu(!showStrokeMenu)}
          title="Stroke Style"
          disabled={hasMixedValues('strokeStyle')}
        >
          <GripVertical size={16} opacity={hasMixedValues('strokeStyle') ? 0.5 : 1} />
        </button>
        {showStrokeMenu && (
          <div
            ref={strokeMenuRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              zIndex: 1001,
            }}
          >
            {strokePatterns.map((pattern) => {
              const isActive =
                strokeStyle?.pattern === pattern.pattern && !hasMixedValues('strokeStyle');
              return (
                <button
                  key={pattern.pattern}
                  style={{
                    ...buttonStyle(isActive),
                    width: '80px',
                    height: '28px',
                    justifyContent: 'flex-start',
                    padding: '0 8px',
                  }}
                  onClick={() => {
                    const newStyle: StrokeStyle = {
                      pattern: pattern.pattern,
                      align: strokeStyle?.align || 'center',
                    };
                    onUpdate({ strokeStyle: newStyle });
                    setShowStrokeMenu(false);
                  }}
                  title={pattern.label}
                >
                  <Minus
                    size={14}
                    strokeDasharray={
                      pattern.pattern === 'dashed' ? '5,5' : pattern.pattern === 'dotted' ? '2,5' : undefined
                    }
                  />
                  <span style={{ marginLeft: '8px', fontSize: '12px' }}>{pattern.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={dividerStyle} />

      {/* Shadow */}
      {/* <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(
            !!(shadows && shadows.length > 0) && !hasMixedValues('shadows')
          )}
          onClick={() => setShowShadowMenu(!showShadowMenu)}
          title="Shadow Effects"
          disabled={hasMixedValues('shadows')}
        >
          <Layers size={16} opacity={hasMixedValues('shadows') ? 0.5 : 1} />
        </button>
        {showShadowMenu && (
          <div
            ref={shadowMenuRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              zIndex: 1001,
              minWidth: '200px',
            }}
          >
            <div style={{ fontSize: '12px', fontWeight: '500' }}>Shadow Effects</div>
            <button
              style={{
                ...buttonStyle(!!(shadows && shadows.length > 0)),
                width: '100%',
                height: '32px',
                justifyContent: 'flex-start',
                padding: '0 8px',
              }}
              onClick={() => {
                if (shadows && shadows.length > 0) {
                  onUpdate({ shadows: [] });
                } else {
                  const newShadow = createDefaultShadow('drop');
                  onUpdate({ shadows: [newShadow] });
                }
                setShowShadowMenu(false);
              }}
            >
              {shadows && shadows.length > 0 ? 'Remove Shadow' : 'Add Drop Shadow'}
            </button>
          </div>
        )}
      </div> */}

      {/* <div style={dividerStyle} /> */}

      {/* Blur */}
      {/* <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(
            !!(blur && blur.radius > 0) && !hasMixedValues('blur')
          )}
          onClick={() => setShowBlurMenu(!showBlurMenu)}
          title="Blur Effects"
          disabled={hasMixedValues('blur')}
        >
          <Droplet size={16} opacity={hasMixedValues('blur') ? 0.5 : 1} />
        </button>
        {showBlurMenu && (
          <div
            ref={blurMenuRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              zIndex: 1001,
              minWidth: '200px',
            }}
          >
            <div style={{ fontSize: '12px', fontWeight: '500' }}>Blur Effects</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', minWidth: '50px' }}>Radius:</span>
              <input
                type="range"
                min="0"
                max="50"
                step="1"
                value={blur?.radius || 0}
                onChange={(e) => {
                  const radius = parseInt(e.target.value);
                  if (radius > 0) {
                    onUpdate({ blur: { type: blur?.type || 'layer', radius } });
                  } else {
                    onUpdate({ blur: undefined });
                  }
                }}
                style={{ flex: 1 }}
                disabled={hasMixedValues('blur')}
              />
              <span style={{ fontSize: '12px', minWidth: '30px' }}>{blur?.radius || 0}px</span>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                style={{
                  ...buttonStyle(blur?.type === 'layer'),
                  flex: 1,
                  height: '28px',
                }}
                onClick={() => {
                  const currentRadius = blur?.radius || 10;
                  onUpdate({ blur: { type: 'layer', radius: currentRadius } });
                }}
              >
                Layer Blur
              </button>
              <button
                style={{
                  ...buttonStyle(blur?.type === 'background'),
                  flex: 1,
                  height: '28px',
                }}
                onClick={() => {
                  const currentRadius = blur?.radius || 10;
                  onUpdate({ blur: { type: 'background', radius: currentRadius } });
                }}
              >
                Background Blur
              </button>
            </div>
            {blur && blur.radius > 0 && (
              <button
                style={{
                  ...buttonStyle(false),
                  width: '100%',
                  height: '28px',
                  marginTop: '4px',
                }}
                onClick={() => {
                  onUpdate({ blur: undefined });
                  setShowBlurMenu(false);
                }}
              >
                Remove Blur
              </button>
            )}
          </div>
        )}
      </div> */}

      {/* Blend Mode */}
      {/* <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(
            !!(blendMode && blendMode !== 'normal') && !hasMixedValues('blendMode')
          )}
          onClick={() => setShowBlendMenu(!showBlendMenu)}
          title="Blend Mode"
          disabled={hasMixedValues('blendMode')}
        >
          <Eye size={16} opacity={hasMixedValues('blendMode') ? 0.5 : 1} />
        </button>
        {showBlendMenu && (
          <div
            ref={blendMenuRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              zIndex: 1001,
              maxHeight: '300px',
              overflowY: 'auto',
              minWidth: '150px',
            }}
          >
            {([
              'normal',
              'multiply',
              'screen',
              'overlay',
              'darken',
              'lighten',
              'color-dodge',
              'color-burn',
              'hard-light',
              'soft-light',
              'difference',
              'exclusion',
            ] as BlendMode[]).map((mode) => {
              const isActive = blendMode === mode && !hasMixedValues('blendMode');
              return (
                <button
                  key={mode}
                  style={{
                    ...buttonStyle(isActive),
                    width: '100%',
                    height: '28px',
                    justifyContent: 'flex-start',
                    padding: '0 8px',
                    textTransform: 'capitalize',
                  }}
                  onClick={() => {
                    onUpdate({ blendMode: mode });
                    setShowBlendMenu(false);
                  }}
                  title={mode}
                >
                  {mode.replace('-', ' ')}
                </button>
              );
            })}
          </div>
        )}
      </div> */}

      {/* <div style={dividerStyle} /> */}

      {/* Opacity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={opacity ?? 1}
          onChange={(e) => onUpdate({ opacity: parseFloat(e.target.value) })}
          style={{ width: '60px' }}
          title={`Opacity: ${Math.round((opacity ?? 1) * 100)}%`}
          disabled={hasMixedValues('opacity')}
        />
        <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '35px' }}>
          {Math.round((opacity ?? 1) * 100)}%
        </span>
      </div>

      {/* Shape-Specific Controls */}
      {shapeType && (
        <>
          <div style={dividerStyle} />
          
          {/* Rectangle: Corner Radius */}
          {(shapeType === 'rectangle' || shapeType === 'rectangle-rounded' || shapeType === 'rectangle-pill') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '50px' }}>
                Radius:
              </span>
              <input
                type="range"
                min="0"
                max="50"
                step="1"
                value={
                  typeof cornerRadius === 'number' 
                    ? cornerRadius 
                    : cornerRadius?.uniform || 0
                }
                onChange={(e) => {
                  const radius = parseInt(e.target.value);
                  onUpdate({ cornerRadius: radius });
                }}
                style={{ width: '60px' }}
                title="Corner Radius"
                disabled={hasMixedValues('cornerRadius')}
              />
              <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '25px' }}>
                {typeof cornerRadius === 'number' ? cornerRadius : cornerRadius?.uniform || 0}px
              </span>
            </div>
          )}

          {/* Star: Points and Inner Radius */}
          {shapeType === 'star' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '150px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '60px' }}>
                  Points:
                </span>
                <input
                  type="number"
                  min="3"
                  max="20"
                  value={starConfig?.numPoints || 5}
                  onChange={(e) => {
                    const numPoints = Math.max(3, Math.min(20, parseInt(e.target.value) || 5));
                    onUpdate({ starConfig: { ...starConfig, numPoints } });
                  }}
                  style={{ width: '60px', padding: '2px 4px', border: '1px solid rgb(229, 231, 235)', borderRadius: '4px' }}
                  disabled={hasMixedValues('starConfig')}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '60px' }}>
                  Inner:
                </span>
                <input
                  type="range"
                  min="0.1"
                  max="0.9"
                  step="0.05"
                  value={starConfig?.innerRadiusRatio || 0.5}
                  onChange={(e) => {
                    const ratio = parseFloat(e.target.value);
                    onUpdate({ starConfig: { ...starConfig, innerRadiusRatio: ratio } });
                  }}
                  style={{ flex: 1 }}
                  disabled={hasMixedValues('starConfig')}
                />
                <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '30px' }}>
                  {Math.round((starConfig?.innerRadiusRatio || 0.5) * 100)}%
                </span>
              </div>
            </div>
          )}

          {/* Polygon: Sides */}
          {shapeType === 'polygon' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '50px' }}>
                Sides:
              </span>
              <input
                type="number"
                min="3"
                max="20"
                value={polygonConfig?.sides || 6}
                onChange={(e) => {
                  const sides = Math.max(3, Math.min(20, parseInt(e.target.value) || 6));
                  onUpdate({ polygonConfig: { sides } });
                }}
                style={{ width: '60px', padding: '2px 4px', border: '1px solid rgb(229, 231, 235)', borderRadius: '4px' }}
                disabled={hasMixedValues('polygonConfig')}
              />
            </div>
          )}

          {/* Arrow: Configurable arrowhead and double-ended */}
          {shapeType === 'arrow' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '150px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '70px' }}>
                  Length:
                </span>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="1"
                  value={arrowConfig?.pointerLength || 20}
                  onChange={(e) => {
                    const length = parseInt(e.target.value);
                    onUpdate({ arrowConfig: { ...arrowConfig, pointerLength: length } });
                  }}
                  style={{ flex: 1 }}
                  disabled={hasMixedValues('arrowConfig')}
                />
                <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '30px' }}>
                  {arrowConfig?.pointerLength || 20}px
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '70px' }}>
                  Width:
                </span>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="1"
                  value={arrowConfig?.pointerWidth || 20}
                  onChange={(e) => {
                    const width = parseInt(e.target.value);
                    onUpdate({ arrowConfig: { ...arrowConfig, pointerWidth: width } });
                  }}
                  style={{ flex: 1 }}
                  disabled={hasMixedValues('arrowConfig')}
                />
                <span style={{ fontSize: '12px', color: 'rgb(107, 114, 128)', minWidth: '30px' }}>
                  {arrowConfig?.pointerWidth || 20}px
                </span>
              </div>
              <button
                style={buttonStyle(arrowConfig?.doubleEnded || false)}
                onClick={() => {
                  onUpdate({ arrowConfig: { ...arrowConfig, doubleEnded: !arrowConfig?.doubleEnded } });
                }}
                title="Double-Ended Arrow"
                disabled={hasMixedValues('arrowConfig')}
              >
                <ArrowRight size={16} style={{ transform: 'scaleX(-1)' }} />
                <ArrowRight size={16} style={{ marginLeft: '-8px' }} />
              </button>
            </div>
          )}
        </>
      )}

      {onClose && (
        <>
          <div style={dividerStyle} />
          <button
            style={buttonStyle(false)}
            onClick={onClose}
            title="Close"
          >
            <X size={16} />
          </button>
        </>
      )}
    </div>
  );
};


